import { BalanceData, OfferScreen, SkinData, SkinKind, Wallet, metaSeed, purchasesAllowed, seasonTheme, utcDay } from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Cosmetics (FINISH_PLAN WP8, DESIGN §12 Skins): which skins the player has and wears, as use cases over the catalog
 * and its wallet, saved in the `cosmetics` save section. The catalogue is shop.json `skins` (data/commerce.ts); the
 * looks are the web's `render/skins/`. Nothing here is a sim input: the level is built from the profile and the gear,
 * never from this state, so equipping a skin cannot change a replay (test/cosmetics.test.ts replays the goldens).
 *
 * A skin is had one of six ways: a level milestone (a tower skin for a tower once THAT tower reaches the level, any
 * other kind at the Fortress level — derived, never stored), bought for gems or Marks (stored in `owned`), bought
 * from the Black Market on a day it features it (stored), an event's emblem (read from the `events` section's
 * emblems), or a season skin paid by the War Campaign's Premium cosmetic tier (stored: app/season.ts unlocks it through
 * PassCosmetics). Every kind's stock look is always had; "stock" is stored as absent. M40 (docs/RARES.md): the Starfall
 * set is had only by slaying the Starfall Wyrm (app wyrm.ts grants one into `owned`); each of its tower skins is worn
 * only on its own tower type (`SkinData.tower`). MP-23: the Trophy Hall's exclusives (`trophies` unlock: banners, roads,
 * title frames, kill effects) are bought there for Trophies and granted into `owned` by skin id (LevelCatalog
 * `unlockSkin`, idempotent); frames and kill effects are worn once for the whole game like the other single kinds.
 * Pets v2 (docs/PETS.md): a pet skin fits one pet (`SkinData.pet`) and is worn per pet; it is had only by catching its
 * pet wearing it in battle (app pets.ts unlocks it into `owned`, like the Wyrm's).
 */

export interface CosmeticsJson {
  /** Skins bought (gems, Marks, the Black Market), paid by the season pass, or migrated. */
  owned: string[];
  /** Tower id → the skin it wears. */
  towers: Record<string, string>;
  fortress?: string;
  troop?: string;
  emblem?: string;
  road?: string;
  /** MP-23 (Trophy Hall): the worn title frame and kill effect (absent in older saves: stock, none). */
  frame?: string;
  kill?: string;
  /** Pet id → the skin it wears. */
  pets?: Record<string, string>;
  /** Skins had and already looked at in the picker ("id", or "id@tower" for a milestone tower skin): the rest are new. */
  seen?: string[];
}

/** The kinds worn once for the whole game (a tower skin is worn per tower type, a pet skin per pet). */
export type SingleKind = Exclude<SkinKind, "tower" | "pet">;
const SINGLE_KINDS: readonly SingleKind[] = ["fortress", "troop", "emblem", "road", "frame", "kill"];

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const cosmeticsSection = registerSaveSection<CosmeticsJson>("cosmetics", asObject, (v) => v);

/** Seed tag of the Black Market's skin roll (apart from its gear roll and every other meta roll). */
const BLACK_MARKET_SKIN_TAG = "blackMarketSkin";
const PERCENT = 100n;

export class CosmeticsState {
  readonly owned = new Set<string>();
  readonly towers = new Map<string, string>();
  readonly pets = new Map<string, string>();
  readonly worn: Record<SingleKind, string | null> = { fortress: null, troop: null, emblem: null, road: null, frame: null, kill: null };
  readonly seen = new Set<string>();

  toJson(): CosmeticsJson {
    const j: CosmeticsJson = { owned: [...this.owned].sort(), towers: Object.fromEntries([...this.towers].sort(([a], [b]) => a.localeCompare(b))) };
    for (const k of SINGLE_KINDS) { const v = this.worn[k]; if (v) j[k] = v; }
    if (this.pets.size > 0) j.pets = Object.fromEntries([...this.pets].sort(([a], [b]) => a.localeCompare(b)));
    if (this.seen.size > 0) j.seen = [...this.seen].sort();
    return j;
  }

  /** Leniently, field by field: unknown ids, a skin on the wrong kind and a skin on an unknown tower are dropped. */
  static fromJson(balance: BalanceData, src: Partial<CosmeticsJson> | undefined): CosmeticsState {
    const s = new CosmeticsState();
    const byId = new Map(balance.commerce.shop.skins.list.map((k) => [k.id, k]));
    const is = (id: unknown, kind: SkinKind): id is string => typeof id === "string" && byId.get(id)?.kind === kind;
    if (!src) return s;
    if (Array.isArray(src.owned)) for (const id of src.owned) if (typeof id === "string" && byId.has(id)) s.owned.add(id);
    const towerIds = new Set(balance.towers.filter((t) => !t.isBase).map((t) => t.id));
    const towers = asObject<Record<string, unknown>>(src.towers);
    if (towers) for (const [t, id] of Object.entries(towers)) if (towerIds.has(t) && is(id, "tower") && (byId.get(id)!.tower ?? t) === t) s.towers.set(t, id);
    for (const k of SINGLE_KINDS) { const id = src[k]; if (is(id, k)) s.worn[k] = id; }
    const pets = asObject<Record<string, unknown>>(src.pets);
    if (pets) for (const [p, id] of Object.entries(pets)) if (is(id, "pet") && byId.get(id)!.pet === p) s.pets.set(p, id);
    if (Array.isArray(src.seen)) for (const key of src.seen) if (typeof key === "string" && byId.has(key.split("@")[0])) s.seen.add(key);
    return s;
  }

  /**
   * The save's cosmetics, or — for a save from before them — a fresh state that carries over the fortress skin the old
   * device toggle wore (`legacyFortress`, localStorage `td.baseSkin`: a model suffix such as "dragonlord"), owned and
   * worn. `migrated` says the legacy value was used, so the caller can drop it.
   */
  static fromSave(balance: BalanceData, save: HasSections, legacyFortress: string | null = null): { state: CosmeticsState; migrated: boolean } {
    const json = cosmeticsSection.read(save);
    if (json) return { state: CosmeticsState.fromJson(balance, json), migrated: false };
    const s = new CosmeticsState();
    const id = legacyFortress ? `fortress.${legacyFortress}` : null;
    if (id && balance.commerce.shop.skins.list.some((k) => k.id === id && k.kind === "fortress")) {
      s.owned.add(id);
      s.worn.fortress = id;
      return { state: s, migrated: true };
    }
    return { state: s, migrated: false };
  }

  applyTo(save: HasSections): void { cosmeticsSection.write(save, this.toJson()); }
}

/** Whether the player has a skin, and if not what it takes. `towerId`: the tower a tower skin is asked about. */
export type SkinAccess =
  | { readonly have: true }
  | { readonly have: false; readonly by: "level"; readonly level: number; readonly current: number; readonly fortress: boolean }
  | { readonly have: false; readonly by: "gems" | "marks"; readonly price: number; readonly affordable: boolean }
  | { readonly have: false; readonly by: "blackMarket"; readonly price: number; readonly affordable: boolean; readonly today: boolean }
  | { readonly have: false; readonly by: "event"; readonly emblem: string }
  /** A season skin: the War Campaign season (1-based), its name, and the Premium tier that pays it. */
  | { readonly have: false; readonly by: "season"; readonly season: number; readonly name: string; readonly tier: number }
  /** M40: a Starfall skin, had only by slaying the Starfall Wyrm. */
  | { readonly have: false; readonly by: "wyrm" }
  /** MP-23: a Trophy Hall exclusive, bought there for Trophies (the Hall buys it and grants it through `unlockSkin`). */
  | { readonly have: false; readonly by: "trophies"; readonly price: number }
  /** Pets v2: a pet skin, had only by catching `pet` wearing it in battle. */
  | { readonly have: false; readonly by: "caught"; readonly pet: string };

/**
 * Whether `s` fits `who` (null: any): a tower skin with a `tower` (M40 Starfall) fits that tower type alone, a pet skin
 * its own pet alone (`who` is the tower id for a tower skin, the pet id for a pet skin).
 */
function fits(s: SkinData, who: string | null): boolean {
  if (who === null) return true;
  if (s.kind === "pet") return s.pet === who;
  return s.tower === undefined || s.tower === who;
}

export class Cosmetics {
  constructor(private readonly catalog: LevelCatalog, readonly state: CosmeticsState) {}

  private get balance(): BalanceData { return this.catalog.balance; }
  get wallet(): Wallet { return this.catalog.economy.wallet; }
  get catalogue(): readonly SkinData[] { return this.balance.commerce.shop.skins.list; }
  skin(id: string): SkinData | null { return this.catalogue.find((s) => s.id === id) ?? null; }
  /** The catalogue's skins of `kind`; with `towerId` (the pet id for "pet"), only those it may wear. */
  list(kind: SkinKind, towerId: string | null = null): SkinData[] { return this.catalogue.filter((s) => s.kind === kind && fits(s, towerId)); }

  /** A tower's persistent level, or the Fortress's for every other kind. */
  private levelFor(kind: SkinKind, towerId: string | null): number {
    const p = this.catalog.profile;
    if (kind !== "tower") return p.baseLevel;
    const i = this.balance.towers.findIndex((t) => t.id === towerId && !t.isBase);
    return i < 0 ? 0 : p.towerLevels[i];
  }

  access(id: string, towerId: string | null = null, now = 0): SkinAccess {
    const s = this.skin(id);
    if (!s) return { have: false, by: "event", emblem: "" };
    const u = s.unlock;
    if (this.state.owned.has(id)) return { have: true };
    switch (u.by) {
      case "level": {
        const current = this.levelFor(s.kind, towerId);
        return current >= u.level ? { have: true } : { have: false, by: "level", level: u.level, current, fortress: s.kind !== "tower" };
      }
      case "event": return this.catalog.endgame.events.emblems.includes(u.emblem) ? { have: true } : { have: false, by: "event", emblem: u.emblem };
      case "season": {
        const d = this.balance.seasons;
        return { have: false, by: "season", season: u.season, name: seasonTheme(d, u.season - 1).name, tier: d.premiumTrack.find((t) => t.cosmetic)?.tier ?? d.tiers };
      }
      case "blackMarket": return { have: false, by: "blackMarket", price: u.price, affordable: this.wallet.canAfford("gems", u.price), today: this.blackMarketSkin(now)?.id === id };
      case "wyrm": return { have: false, by: "wyrm" };
      case "trophies": return { have: false, by: "trophies", price: u.price };
      case "caught": return { have: false, by: "caught", pet: s.pet ?? "" };
      default: return { have: false, by: u.by, price: u.price, affordable: this.wallet.canAfford(u.by, u.price) };
    }
  }

  has(id: string, towerId: string | null = null): boolean { return this.access(id, towerId).have; }

  /** The skin a kind (a tower or a pet: `towerId` names it) wears now; null = stock. A skin no longer had reads as stock. */
  worn(kind: SkinKind, towerId: string | null = null): string | null {
    const id = kind === "tower" ? this.state.towers.get(towerId ?? "") ?? null : kind === "pet" ? this.state.pets.get(towerId ?? "") ?? null : this.state.worn[kind];
    return id && this.has(id, towerId) ? id : null;
  }

  /** Wears `id` (null: the stock look) on `kind` — on `towerId` for a tower skin, on that pet for a pet skin. */
  equip(kind: SkinKind, id: string | null, towerId: string | null = null): true | string {
    if (kind === "tower" && !this.balance.towers.some((t) => t.id === towerId && !t.isBase)) return "no such tower";
    if (kind === "pet" && !this.balance.pets.list.some((p) => p.id === towerId)) return "no such pet";
    if (id !== null) {
      const s = this.skin(id);
      if (!s || s.kind !== kind) return "no such skin";
      if (kind === "tower" && !fits(s, towerId)) return "not for this tower";
      if (kind === "pet" && !fits(s, towerId)) return "not for this pet";
      if (!this.has(id, towerId)) return "locked";
    }
    const perOne = kind === "tower" ? this.state.towers : kind === "pet" ? this.state.pets : null;
    if (perOne) { if (id) perOne.set(towerId!, id); else perOne.delete(towerId!); }
    else this.state.worn[kind as SingleKind] = id;
    return true;
  }

  /** The owned tower types (skins are shown for towers the player has). */
  private ownedTowerIds(): string[] {
    const owned = this.catalog.ownedTowers();
    return this.balance.towers.filter((t, i) => !t.isBase && owned[i]).map((t) => t.id);
  }

  /** The "seen" keys of every skin had now: a milestone tower skin once per tower that reached it, the rest once. */
  private haveKeys(kind?: SkinKind, towerId?: string | null): string[] {
    const keys: string[] = [];
    for (const s of this.catalogue) {
      if ((kind && s.kind !== kind) || !fits(s, towerId ?? null)) continue;
      if (s.kind === "tower" && s.unlock.by === "level") {
        for (const t of towerId ? [towerId] : this.ownedTowerIds()) if (this.has(s.id, t)) keys.push(`${s.id}@${t}`);
      } else if (this.has(s.id)) keys.push(s.id);
    }
    return keys;
  }

  /** Skins had but not yet looked at (the Towers tab's red count, the More row's dot). */
  unseen(): string[] { return this.haveKeys().filter((key) => !this.state.seen.has(key)); }
  /** The picker showed `kind` (for a tower skin, on `towerId`): what is had there is no longer new. True if anything changed. */
  markSeen(kind: SkinKind, towerId: string | null = null): boolean {
    let changed = false;
    for (const key of this.haveKeys(kind, kind === "tower" ? towerId : null)) if (!this.state.seen.has(key)) { this.state.seen.add(key); changed = true; }
    return changed;
  }
  /** Everything had now counts as seen (the first run of skins on an existing save: no wall of badges). */
  markAllSeen(): void { for (const key of this.haveKeys()) this.state.seen.add(key); }
  /** How many catalogue skins the player has on at least one tower (the More row's "n of 16"). */
  haveCount(): number {
    const towers = this.ownedTowerIds();
    return this.catalogue.filter((s) => (s.kind === "tower" && s.unlock.by === "level" ? towers.some((t) => this.has(s.id, t)) : this.has(s.id))).length;
  }

  /**
   * What the skin costs in gems in the Shop (owner 2026-10-21: every skin but the Starfall Wyrm's): its own gems price,
   * or its `buyGems` beside its free route; null for the Wyrm's and once it is had (`towerId` for a milestone tower skin).
   */
  gemPrice(id: string, towerId: string | null = null): number | null {
    const s = this.skin(id);
    if (!s || this.has(id, towerId)) return null;
    if (s.unlock.by === "gems") return s.unlock.price;
    return s.buyGems ?? null;
  }

  /**
   * Buys a Shop skin and keeps it; hub only. The caller persists. `currency` "gems" buys any skin with a gem price
   * (`gemPrice`); "marks" a Marks skin at its Marks price. Default: the skin's own Shop currency, else gems.
   */
  buy(id: string, now: number, screen: OfferScreen = "hub", currency?: "gems" | "marks"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    const s = this.skin(id);
    if (!s) return "no such product";
    if (this.state.owned.has(id)) return "already owned";
    const cur = currency ?? (s.unlock.by === "marks" ? "marks" : "gems");
    const price = cur === "marks" ? (s.unlock.by === "marks" ? s.unlock.price : null) : s.unlock.by === "gems" ? s.unlock.price : s.buyGems ?? null;
    if (price === null) return "no such product";
    const r = this.wallet.spend(cur, price, "skin", now, id);
    if (r !== true) return r;
    this.state.owned.add(id);
    this.state.seen.add(id);
    return true;
  }

  /**
   * The exclusive skin the Black Market features today, beside its gear piece: on `blackMarketPercent` of UTC days,
   * rolled from (player, day), one of the Black Market skins the player lacks; null on other days or once all are had.
   */
  blackMarketSkin(now: number): SkinData | null {
    const cands = this.catalogue.filter((s) => s.unlock.by === "blackMarket" && !this.state.owned.has(s.id));
    const pct = BigInt(this.balance.commerce.shop.skins.blackMarketPercent);
    if (cands.length === 0 || pct <= 0n) return null;
    const roll = metaSeed(this.catalog.playerId, BLACK_MARKET_SKIN_TAG, utcDay(now));
    if (roll % PERCENT >= pct) return null;
    return cands[Number((roll / PERCENT) % BigInt(cands.length))];
  }

  /** Buys today's featured Black Market skin for gems, exactly as shown; hub only. The caller persists. */
  buyBlackMarketSkin(id: string, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    const s = this.blackMarketSkin(now);
    if (!s || s.id !== id || s.unlock.by !== "blackMarket") return "not today";
    const r = this.wallet.spend("gems", s.unlock.price, "skin", now, id);
    if (r !== true) return r;
    this.state.owned.add(id);
    this.state.seen.add(id);
    return true;
  }
}
